import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import CouncilHeroScene from "@/components/home/CouncilHeroScene";
import DifferentiationScene from "@/components/home/DifferentiationScene";
import HowItWorksScene from "@/components/home/HowItWorksScene";
import UseCasesScene from "@/components/home/UseCasesScene";
import PersonaScene from "@/components/home/PersonaScene";
import NotForYouScene from "@/components/home/NotForYouScene";
import TrustScene from "@/components/home/TrustScene";
import FreePaidScene from "@/components/home/FreePaidScene";
import ActivismScene from "@/components/home/ActivismScene";
import FinalCTAScene from "@/components/home/FinalCTAScene";
import StickyCTA from "@/components/home/StickyCTA";
import PullQuoteScene from "@/components/home/PullQuoteScene";
import { isOnboardingSkipped } from "@/lib/onboarding-state";

// Stable kebab-case ids. Used for scroll targeting.

const sectionIds = [
  "hero",
  "problem",
  "pull-quote-1",
  "differentiation",
  "how-it-works",
  "use-cases",
  "personas",
  "not-for-you",
  "trust",
  "pull-quote-2",
  "free-vs-circle",
  "values",
  "start",
];

const Index = () => {
  const [stickyVisible, setStickyVisible] = useState(false);
  const location = useLocation();

  // Auto-redirect logged-in users (skip if they explicitly navigated here).
  // New accounts — including Google OAuth returns, which land on "/" — go to
  // onboarding until profiles.onboarding_completed_at is stamped.
  const { user, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const explicitNav = (location.state as any)?.fromNav === true;

  useEffect(() => {
    if (explicitNav || authLoading || !user || !profile) return;
    if (!profile.onboarding_completed_at && !isOnboardingSkipped()) {
      navigate("/onboarding", { replace: true });
      return;
    }
    navigate("/chamber", { replace: true });
  }, [user, profile, authLoading, navigate, explicitNav]);


  // Deep-link to newsletter section (FinalCTA / "start")
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("section") === "newsletter") {
      const timer = setTimeout(() => {
        document.getElementById("start")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
      window.history.replaceState({}, "", "/");
      return () => clearTimeout(timer);
    }
  }, [location.search]);

  // Sticky CTA is shown only when neither the hero primary CTA nor the final
  // CTA button is in the viewport. IntersectionObserver only — no scroll handlers.
  useEffect(() => {
    const ids = ["hero-primary-cta", "final-primary-cta"];
    const inView = new Map<string, boolean>();

    let observer: IntersectionObserver | null = null;
    let raf = 0;

    const attach = () => {
      const els = ids
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => !!el);
      if (els.length < ids.length) {
        raf = requestAnimationFrame(attach);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => inView.set(entry.target.id, entry.isIntersecting));
          setStickyVisible(!ids.some((id) => inView.get(id)));
        },
        { threshold: 0 },
      );
      els.forEach((el) => observer!.observe(el));
    };
    attach();

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, []);


  const navigateTo = useCallback((index: number) => {
    const id = sectionIds[index];
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <>
      <PageHead
        title="Outspoken — A private decision companion for modern Indian life"
        description="Outspoken helps you think clearly about career, relationships, money, and life direction — using Indian wisdom from the Bhagavad Gita, Arthashastra, Ramayan, and Mahabharata, translated into plain language."
        canonicalPath="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Outspoken",
            "url": "https://outspoken.co.in",
            "description": "A private decision companion built on Indian wisdom — the Bhagavad Gita, Arthashastra, Ramayan, and Mahabharata — translated into clear, practical language for real-life decisions.",
            "foundingDate": "2026",
            "keywords": "decision making, clarity, Indian wisdom, Bhagavad Gita, Chanakya, Arthashastra, career advice, relationship clarity"
          },
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Outspoken",
            "url": "https://outspoken.co.in/coach",
            "applicationCategory": "LifestyleApplication",
            "description": "A private decision companion. Choose the Bhagavad Gita, Chanakya, Vidur, or Ramayan lens for career, relationship, money, and life decisions.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "INR",
              "description": "3 free Sessions per week. Unlimited Sessions with Circle membership."
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is Outspoken?", "acceptedAnswer": { "@type": "Answer", "text": "Outspoken is a private decision companion that helps you think clearly about career, relationships, money, family, and life direction. It draws on Indian wisdom systems — the Bhagavad Gita, Arthashastra, Ramayan, and Mahabharata — translated into plain, practical language. It is not astrology and not therapy." } },
              { "@type": "Question", "name": "How is Outspoken different from astrology apps?", "acceptedAnswer": { "@type": "Answer", "text": "Outspoken does not predict your future, read birth charts, or offer remedies. It helps you think through the decision in front of you using time-tested Indian frameworks — so you can decide better, not be told what will happen." } },
              { "@type": "Question", "name": "Which lenses can I choose from?", "acceptedAnswer": { "@type": "Answer", "text": "The Bhagavad Gita lens for clarity under pressure, Chanakya for strategy and smart career moves, Vidur for honest judgment in difficult situations, and the Ramayan lens for duty and decisions that must stay clean." } },
              { "@type": "Question", "name": "Is Outspoken a religious platform?", "acceptedAnswer": { "@type": "Answer", "text": "No. Outspoken treats these texts as practical decision-making systems — not as religious instruction. No sermons, no astrology, no rituals." } },
              { "@type": "Question", "name": "What is the Circle membership?", "acceptedAnswer": { "@type": "Answer", "text": "Circle gives you unlimited Sessions, memory across conversations, and weekly Reflections, with early access to the Dojo when it launches. Free users get 3 Sessions per week." } },
              { "@type": "Question", "name": "What kinds of decisions can Outspoken help with?", "acceptedAnswer": { "@type": "Answer", "text": "Career confusion, relationship confusion, money pressure, family pressure, life direction, and high-stakes decisions — anything where you want to think straight before you act." } }
            ]
          }
        ]}
      />
      <div className="continuous-scroll">
        <CouncilHeroScene onNavigateToSection={navigateTo} />
        <div id="pull-quote-1">
          <PullQuoteScene
            kicker="A reminder"
            quote="You cannot quiet the world. <em>You can quiet your own mind.</em>"
          />
        </div>
        <div id="differentiation"><DifferentiationScene /></div>
        <div id="how-it-works"><HowItWorksScene /></div>
        <div id="use-cases"><UseCasesScene /></div>
        <div id="personas"><PersonaScene /></div>
        <div id="not-for-you"><NotForYouScene /></div>
        <div id="trust"><TrustScene /></div>
        <div id="pull-quote-2">
          <PullQuoteScene
            kicker="Our method"
            quote="We speak only of what is useful — <em>not what sounds clever.</em>"
          />
        </div>
        <div id="free-vs-circle"><FreePaidScene /></div>
        <div id="values"><ActivismScene /></div>
        <div id="start"><FinalCTAScene /></div>
      </div>
      <StickyCTA visible={stickyVisible} />
    </>
  );
};

export default Index;
